import { memo, useCallback, useEffect, useRef } from 'react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import { useSupervisoryOrgOwner } from '../../../../hooks';
import { CicdOidcSubjectsInput, SecurityGroupsInput } from '../components';
import { OWNER_INFO, SECURITY_GROUPS_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import { ProjectOwnerValue } from '../types';
import {
  cicdOidcSubjectsInputErrors,
  ownerInputErrors,
  securityGroupsInputErrors,
} from '../validation';

export interface OwnerSectionProps {
  value: ProjectOwnerValue;
  onChange: (value: ProjectOwnerValue) => void;
  errors?: string[];
}

const OwnerSectionInner = ({ value, onChange, errors }: OwnerSectionProps) => {
  const { classes } = useStyles();

  // Stable refs — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  const hasErrors = Boolean(errors && errors.length > 0);

  const ownerError = hasErrors ? ownerInputErrors(value.owner) : '';
  const hasOwnerError = ownerError !== '';

  const securityGroupsError = hasErrors
    ? securityGroupsInputErrors(value.securityGroups)
    : '';

  const cicdSubjectsError = hasErrors
    ? cicdOidcSubjectsInputErrors(value.cicdOidcSubjects)
    : '';

  const ownerRef = useRef<HTMLDivElement>(null);
  const securityGroupsRef = useRef<HTMLDivElement>(null);
  const cicdSubjectsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasOwnerError) {
      ownerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (securityGroupsError) {
      securityGroupsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else if (cicdSubjectsError) {
      cicdSubjectsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [hasOwnerError, securityGroupsError, cicdSubjectsError]);

  const {
    ownerOptions,
    selectedOwner,
    loading: ownersLoading,
  } = useSupervisoryOrgOwner(value.owner);

  const handleOwnerChange = useCallback((_: unknown, entity: Entity | null) => {
    onChangeRef.current({
      ...valueRef.current,
      owner: entity ? stringifyEntityRef(entity) : '',
    });
  }, []);

  const handleGroupsChange = useCallback((selected: string[]) => {
    onChangeRef.current({
      ...valueRef.current,
      securityGroups: selected,
    });
  }, []);

  const handleCicdSubjectsChange = useCallback((subjects: string[]) => {
    onChangeRef.current({
      ...valueRef.current,
      cicdOidcSubjects: subjects,
    });
  }, []);

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Ownership and access control"
          description="Configure the ownership details for the Deployment as a Service Project"
          variant="h5"
          divider
        />
        <div ref={ownerRef} className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Project owner"
            description="Select the owner of this project. This should be a supervisory org group in the catalog."
            infoDoc={OWNER_INFO}
          />
          <Autocomplete
            size="small"
            value={selectedOwner}
            loading={ownersLoading}
            onChange={handleOwnerChange}
            options={ownerOptions}
            getOptionLabel={option =>
              (option.metadata.title || option.metadata.name) as string
            }
            isOptionEqualToValue={(option, selected) =>
              stringifyEntityRef(option) === stringifyEntityRef(selected)
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Owner"
                variant="outlined"
                required
                size="small"
                error={hasOwnerError}
                helperText={
                  hasOwnerError ? (
                    <ScaffolderFieldError message={ownerError} />
                  ) : undefined
                }
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {ownersLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={({ key, ...optionProps }, option) => (
              <li key={key} {...optionProps}>
                <Box>
                  <Typography variant="body2" noWrap style={{fontWeight: 600}} >
                    {option.metadata.title || option.metadata.name}
                  </Typography>
                  <Typography variant="caption" noWrap color="text.secondary">
                    {stringifyEntityRef(option)}
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText="No owners found"
            loadingText="Loading owners..."
          />
        </div>
        <div ref={securityGroupsRef} className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Project security groups"
            description="Select security groups that allows members to access, edit or delete this project. Your own groups are listed by default; start typing a group name to search all Azure security groups."
            infoDoc={SECURITY_GROUPS_INFO}
          />
          <SecurityGroupsInput
            title="Security Groups"
            formData={value.securityGroups || []}
            onChange={handleGroupsChange}
            required
            excludeWellKnownGroups
            rawErrors={securityGroupsError ? [securityGroupsError] : []}
          />
        </div>
        <div ref={cicdSubjectsRef}>
          <CicdOidcSubjectsInput
            formData={value.cicdOidcSubjects || []}
            onChange={handleCicdSubjectsChange}
            rawErrors={cicdSubjectsError ? [cicdSubjectsError] : []}
          />
          {cicdSubjectsError && (
            <Box className={classes.inputContainer}>
              <ScaffolderFieldError message={cicdSubjectsError} />
            </Box>
          )}
        </div>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const OwnerSection = memo(OwnerSectionInner);
